import React from 'react';
import Head from 'next/head';
import _ from 'lodash';

import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';

import { hasStructuredData, serializeJsonLd } from './has-structured-data';
import buildDispensaryJsonLd from './build-dispensary-json-ld';
import buildProductJsonLd from './build-product-json-ld';
import buildCategoryPageJsonLd from './build-category-page-json-ld';
import useStoreFrontAbsoluteUrl from '../use-store-front-absolute-url';

const builders = {
  dispensary: buildDispensaryJsonLd,
  product: buildProductJsonLd,
  categoryPage: buildCategoryPageJsonLd,
};

type JsonLDVariant = keyof typeof builders;

type JsonLDScriptProps = {
  variant: JsonLDVariant;
  // The builders are legacy JS and consume dispensary/product/category payloads with
  // distinct shapes, so the data stays structural rather than variant-discriminated.
  jsonLdData: Record<string, unknown>;
};

const JsonLDScript = ({ variant, jsonLdData }: JsonLDScriptProps): JSX.Element | null => {
  const { metaFields } = useCommerceComponents();
  const storeFrontAbsoluteUrl = useStoreFrontAbsoluteUrl();
  const buildJsonLd = builders[variant];

  // The remote theme owns structured data (JSON-LD) when getStoreFrontMetaFields returns it.
  // Suppress host-computed JSON-LD so only the theme-supplied block reaches the DOM (ECOMM-14263).
  if (hasStructuredData(metaFields?.structuredData)) {
    return null;
  }

  if (!buildJsonLd) {
    console.error(`Unsupported ${variant} variant passed to JsonLDScript`);

    return null;
  }

  if (_.isEmpty(jsonLdData)) {
    console.error(`Empty ${variant} data passed to JsonLDScript`);

    return null;
  }

  return (
    <Head>
      <script
        key={`json-ld-${variant}`}
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(buildJsonLd(jsonLdData, storeFrontAbsoluteUrl)) }}
      />
    </Head>
  );
};

export default JsonLDScript;
